import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  Box,
  ExternalLink,
  FileText,
  History,
  Loader2,
  Pause,
  Play,
  Radio,
  Trash2,
  X,
  Info,
} from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import {
  type Device,
  type Sensor,
  type SensorTag,
  type SimulatorChannel,
  deleteSensorTag,
  getSensor,
  listSensorTags,
  listSimulatorChannels,
  simulateTick,
  updateSensor,
} from "../../lib/instrumentationApi";
import api from "../../lib/api";
import { formatDate } from "../../lib/utils";
import { SearchableAssetPicker, type AssetOption } from "./SearchableAssetPicker";
import { DMSDocumentsPanel } from "../dms/DMSDocumentsPanel";

type Tab = "details" | "documents" | "tags" | "history";

interface SensorDetailDrawerProps {
  sensor: Sensor;
  device: Device;
  onClose: () => void;
  /** Notify parent so device/sensor lists can refresh after edits/deletes. */
  onChanged: () => void;
  /** Layout mode:
   *   - "drawer" (default): floating panel anchored to the right edge.
   *   - "pane": fills its container; used when the parent renders a real
   *     right sidebar in flow rather than overlay.
   *
   *  Behaviourally identical — only the outer wrapper's positioning
   *  changes. */
  variant?: "drawer" | "pane";
}

/** Side drawer that opens when the user clicks a sensor row on the
 *  Instrumentation page. Contains four tabs:
 *
 *   - Details: editable sensor metadata + live mini-plot of recent data
 *   - Documentation: upload/list/delete S3-hosted datasheets and certs
 *   - 3D Tags: where the sensor lives on point clouds / digital twins
 *   - History: full reading history (line plot + table)
 *
 *  The live data poller is driven by SensorDataPanel-style logic but
 *  keyed to the Details tab so the drawer only churns the network when
 *  the user is actually looking at the live view. */
export function SensorDetailDrawer({
  sensor: initialSensor,
  device,
  onClose,
  onChanged,
  variant = "drawer",
}: SensorDetailDrawerProps) {
  const [tab, setTab] = useState<Tab>("details");
  const [sensor, setSensor] = useState<Sensor>(initialSensor);
  const [isPaused, setIsPaused] = useState(false);
  const isSimulated = device.source_type === "simulated";

  // Re-seed when the parent switches sensors.
  useEffect(() => {
    setSensor(initialSensor);
  }, [initialSensor]);

  const tick = useCallback(async () => {
    try {
      if (isSimulated) {
        const fresh = await simulateTick(sensor.id, { count: 1, history_limit: 200 });
        setSensor(fresh);
      } else {
        const fresh = await getSensor(sensor.id);
        setSensor(fresh);
      }
    } catch {
      // ignore per-tick failures
    }
  }, [isSimulated, sensor.id]);

  useEffect(() => {
    if (isPaused) return;
    const id = window.setInterval(tick, 2000);
    tick();
    return () => window.clearInterval(id);
  }, [isPaused, tick]);

  const outerClass =
    variant === "drawer"
      ? "absolute inset-y-0 right-0 z-30 flex w-[480px] max-w-[90vw] flex-col border-l border-surface-700/60 bg-surface-950 shadow-2xl"
      : "flex h-full w-full flex-col bg-surface-950";

  return (
    <div className={outerClass}>
      <div className="flex items-center gap-3 border-b border-surface-700/60 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500/30 to-cyan-500/30 ring-1 ring-white/10">
          <Radio className="h-4 w-4 text-cyan-200" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-surface-50">{sensor.name}</h2>
          <p className="truncate text-[11px] text-surface-400">
            {device.name}
            {sensor.sensor_type ? ` · ${sensor.sensor_type}` : ""}
            {sensor.unit ? ` · ${sensor.unit}` : ""}
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1.5 text-surface-400 hover:bg-white/5 hover:text-surface-100"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex shrink-0 border-b border-surface-700/60 bg-surface-900/40 px-2">
        <TabButton active={tab === "details"} onClick={() => setTab("details")} icon={Info} label="Details" />
        <TabButton active={tab === "documents"} onClick={() => setTab("documents")} icon={FileText} label="Documentation" />
        <TabButton active={tab === "tags"} onClick={() => setTab("tags")} icon={Box} label="3D Tags" />
        <TabButton active={tab === "history"} onClick={() => setTab("history")} icon={History} label="History" />
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === "details" && (
          <DetailsTab
            sensor={sensor}
            device={device}
            isPaused={isPaused}
            onTogglePause={() => setIsPaused((v) => !v)}
            onSensorUpdated={(fresh) => {
              setSensor(fresh);
              onChanged();
            }}
          />
        )}
        {tab === "documents" && (
          <DocumentsTab sensor={sensor} device={device} />
        )}
        {tab === "tags" && (
          <TagsTab sensor={sensor} onChanged={onChanged} />
        )}
        {tab === "history" && (
          <HistoryTab sensor={sensor} />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active, onClick, icon: Icon, label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Activity;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "relative flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors " +
        (active
          ? "border-brand-400 text-brand-200"
          : "border-transparent text-surface-400 hover:text-surface-200")
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

// ── Details tab ───────────────────────────────────────────────────────

function DetailsTab({
  sensor,
  device,
  isPaused,
  onTogglePause,
  onSensorUpdated,
}: {
  sensor: Sensor;
  device: Device;
  isPaused: boolean;
  onTogglePause: () => void;
  onSensorUpdated: (s: Sensor) => void;
}) {
  const [name, setName] = useState(sensor.name);
  const [sensorType, setSensorType] = useState(sensor.sensor_type ?? "");
  const [unit, setUnit] = useState(sensor.unit ?? "");
  const [status, setStatus] = useState(sensor.status);
  const [sourceChannel, setSourceChannel] = useState(sensor.source_channel ?? "");
  // Editable asset linkage. Empty string = "no asset linked" (the backend
  // currently treats "asset_id absent" as "leave unchanged"; we surface
  // explicit changes only via PUT when the picker value diverges from
  // sensor.asset_id).
  const [assetId, setAssetId] = useState<string>(sensor.asset_id ?? "");
  const [config, setConfig] = useState<string>(
    sensor.config ? JSON.stringify(sensor.config, null, 2) : "",
  );
  const [channels, setChannels] = useState<SimulatorChannel[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Reset the form when the user opens a different sensor. Intentionally
  // keyed only by sensor.id so the form doesn't fight the live-data poller
  // — that updates the sensor row on every tick and we'd lose unsaved
  // edits if every change re-seeded the form.
  useEffect(() => {
    setName(sensor.name);
    setSensorType(sensor.sensor_type ?? "");
    setUnit(sensor.unit ?? "");
    setStatus(sensor.status);
    setSourceChannel(sensor.source_channel ?? "");
    setAssetId(sensor.asset_id ?? "");
    setConfig(sensor.config ? JSON.stringify(sensor.config, null, 2) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensor.id]);

  useEffect(() => {
    if (device.source_type !== "simulated") return;
    listSimulatorChannels()
      .then(setChannels)
      .catch(() => setChannels([]));
  }, [device.source_type]);

  // Asset picker: pulled from the device's virtual_site so the dropdown
  // only ever offers same-site assets (the backend rejects cross-site
  // links with a 400, but pre-filtering keeps the form honest).
  useEffect(() => {
    let cancelled = false;
    api
      .get("/assets", {
        // 500 matches the backend's `le` cap on /assets page_size and the
        // other Instrumentation pickers' fetch size. Anything larger
        // (e.g. 1000) would 422 silently.
        params: { virtual_site_id: device.virtual_site_id, page_size: 500 },
      })
      .then((res) => {
        if (cancelled) return;
        const items = (res.data?.items || []) as AssetOption[];
        setAssets(items);
      })
      .catch((err) => {
        console.error("Failed to load assets for sensor detail picker:", err);
        if (!cancelled) setAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [device.virtual_site_id]);

  const latest = useMemo(() => {
    const raw = sensor.data || {};
    const value = (raw as { value?: unknown }).value;
    const ts = (raw as { ts?: string }).ts;
    return { value: typeof value === "number" ? value : null, ts: ts || null };
  }, [sensor]);

  const points = useMemo(() => {
    const raw = sensor.data_history?.readings ?? [];
    return raw
      .map((r) => {
        const value = (r as { value?: unknown }).value;
        const ts = (r as { ts?: string }).ts;
        return typeof value === "number"
          ? { value, tsLabel: ts ? new Date(ts).toLocaleTimeString() : "" }
          : null;
      })
      .filter((p): p is { value: number; tsLabel: string } => p !== null);
  }, [sensor]);

  async function handleSave() {
    setIsSaving(true);
    setError(null);
    try {
      let parsedConfig: Record<string, unknown> | null = null;
      if (config.trim()) {
        try {
          parsedConfig = JSON.parse(config);
        } catch {
          setError("Config must be valid JSON.");
          setIsSaving(false);
          return;
        }
      }
      const updatePayload: Record<string, unknown> = {
        name: name.trim(),
        sensor_type: sensorType.trim() || null,
        unit: unit.trim() || null,
        status,
        source_channel: sourceChannel.trim() || null,
        config: parsedConfig,
      };
      // Only send asset_id when the user actually changed it. Sending the
      // existing value is harmless but sending a stale empty would clobber
      // the link inadvertently.
      if (assetId && assetId !== (sensor.asset_id ?? "")) {
        updatePayload.asset_id = assetId;
      }
      const fresh = await updateSensor(sensor.id, updatePayload);
      onSensorUpdated(fresh);
      setSavedAt(Date.now());
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to save sensor.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-4 p-4">
      <div className="rounded-lg border border-surface-700/50 bg-surface-900/50 p-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
            Latest reading
          </span>
          <button
            onClick={onTogglePause}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-surface-300 hover:bg-white/5"
          >
            {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
            {isPaused ? "Paused" : "Live"}
          </button>
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-mono text-2xl font-semibold text-brand-200">
            {latest.value !== null ? latest.value.toFixed(2) : "—"}
          </span>
          {sensor.unit && <span className="text-xs text-surface-300">{sensor.unit}</span>}
        </div>
        {latest.ts && (
          <div className="text-[10px] text-surface-500">
            {formatDate(latest.ts)} · {new Date(latest.ts).toLocaleTimeString()}
          </div>
        )}
        {points.length > 0 && (
          <div className="mt-2 h-24 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 2, right: 4, left: -30, bottom: 0 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis dataKey="tsLabel" tick={{ fontSize: 9, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={20} />
                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} domain={["auto", "auto"]} width={36} />
                <Tooltip
                  contentStyle={{ backgroundColor: "rgba(15,23,42,0.95)", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }}
                />
                <Line type="monotone" dataKey="value" stroke="#a5b4fc" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="space-y-3 rounded-lg border border-surface-700/50 bg-surface-900/40 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-500">
          Sensor metadata
        </div>
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} disabled={isSaving} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Type" value={sensorType} onChange={(e) => setSensorType(e.target.value)} disabled={isSaving} placeholder="e.g. vibration" />
          <Input label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} disabled={isSaving} placeholder="e.g. mm/s" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isSaving} />
          {device.source_type === "simulated" ? (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-surface-200">Simulator channel</label>
              <select
                value={sourceChannel}
                onChange={(e) => setSourceChannel(e.target.value)}
                disabled={isSaving}
                className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">— Auto from sensor type —</option>
                {channels.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label} ({c.unit})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <Input
              label="Source channel"
              value={sourceChannel}
              onChange={(e) => setSourceChannel(e.target.value)}
              disabled={isSaving}
              placeholder="Upstream tag name"
            />
          )}
        </div>
        <div>
          <SearchableAssetPicker
            label="Linked asset"
            assets={assets}
            value={assetId}
            onChange={(id) => setAssetId(id ?? "")}
            disabled={isSaving}
            help="Same-site assets only. Sensors inherit their parent device's asset by default; override here to map a single channel to a different asset."
          />
          {assetId && (
            <a
              href={`#/assets/${assetId}`}
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-300 hover:text-brand-200"
            >
              <ExternalLink className="h-3 w-3" />
              Open asset in register
            </a>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">Config (JSON)</label>
          <textarea
            value={config}
            onChange={(e) => setConfig(e.target.value)}
            disabled={isSaving}
            placeholder='{"mean": 30, "amplitude": 5}'
            className="w-full rounded-lg border border-surface-700 bg-surface-950 px-3 py-2 font-mono text-xs text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            rows={4}
          />
          <p className="mt-1 text-[10px] text-surface-500">
            Calibration parameters, alarm thresholds, simulator tuning (mean / amplitude / noise / spike_chance).
          </p>
        </div>
        {error && <div className="text-xs text-red-400">{error}</div>}
        <div className="flex items-center justify-between pt-1">
          {savedAt && Date.now() - savedAt < 5000 && (
            <span className="text-[11px] text-emerald-300">Saved.</span>
          )}
          <div className="ml-auto">
            <Button onClick={handleSave} isLoading={isSaving}>
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Documents tab (Phase 22: DMS is the single source of truth) ──────

function DocumentsTab({ sensor, device }: { sensor: Sensor; device: Device }) {
  // Sensor uploads land in the canonical DMS folder under
  // /Instrumentation/{device}/{sensor}/ and link automatically to this
  // sensor. Renaming the device or sensor moves the folder + S3 objects
  // via the entity-rename hooks on the backend.
  const deviceLabel = (device.serial_number ?? device.name).trim();
  const sensorLabel = sensor.name.trim();
  return (
    <div className="p-4">
      <DMSDocumentsPanel
        virtualSiteId={device.virtual_site_id}
        entityType="sensor"
        entityId={sensor.id}
        defaultFolderPath={`Instrumentation/${deviceLabel}/${sensorLabel}`}
        title="Documents"
      />
    </div>
  );
}

// ── 3D Tags tab ───────────────────────────────────────────────────────

function TagsTab({ sensor, onChanged }: { sensor: Sensor; onChanged: () => void }) {
  const [tags, setTags] = useState<SensorTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SensorTag | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    listSensorTags({ sensor_id: sensor.id })
      .then(setTags)
      .catch(() => setError("Failed to load 3D tags."))
      .finally(() => setIsLoading(false));
  }, [sensor.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await deleteSensorTag(pendingDelete.id);
      setPendingDelete(null);
      refresh();
      onChanged();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Delete failed.");
    }
  }

  return (
    <div className="space-y-3 p-4">
      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-4 text-xs text-surface-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading 3D tags…
        </div>
      ) : tags.length === 0 ? (
        <div className="rounded-lg border border-dashed border-surface-700 px-4 py-6 text-center text-xs text-surface-500">
          This sensor isn't placed on any 3D model yet.
        </div>
      ) : (
        <div className="space-y-2">
          {tags.map((tag) => {
            const g = tag.geometry as { x?: number; y?: number; z?: number };
            const container = tag.point_cloud_id ? "Point cloud" : "Digital twin";
            const containerId = tag.point_cloud_id || tag.digital_twin_id;
            // Pass ?sensor_tag=<id> so the viewer auto-focuses the camera on
            // this tag's 3D coordinates instead of dumping the user at the
            // model's default view.
            const href = tag.point_cloud_id
              ? `#/point-clouds/${tag.point_cloud_id}?sensor_tag=${tag.id}`
              : `#/digital-twins/${tag.digital_twin_id}?sensor_tag=${tag.id}`;
            return (
              <div
                key={tag.id}
                className="rounded-lg border border-surface-700/50 bg-surface-900/40 px-3 py-2"
              >
                <div className="flex items-start gap-2">
                  <span
                    className="mt-1 h-2 w-2 rounded-full"
                    style={{ backgroundColor: tag.color || "#22d3ee" }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-surface-100">
                        {tag.label || `${container} marker`}
                      </span>
                      <span className="rounded bg-surface-800 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-surface-400">
                        {container}
                      </span>
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] text-surface-500">
                      x: {g.x?.toFixed(3)} · y: {g.y?.toFixed(3)} · z: {g.z?.toFixed(3)}
                    </div>
                    <div className="mt-0.5 text-[10px] text-surface-500">
                      {formatDate(tag.created_at)}
                    </div>
                  </div>
                  <a
                    href={href}
                    className="rounded p-1 text-brand-300 hover:bg-brand-500/15 hover:text-brand-200"
                    title={`Open ${container.toLowerCase()} ${containerId}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <button
                    onClick={() => setPendingDelete(tag)}
                    className="rounded p-1 text-red-400 hover:bg-red-500/15 hover:text-red-300"
                    title="Delete this 3D tag"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Delete 3D tag?"
        message={pendingDelete ? `Delete the 3D placement of ${pendingDelete.sensor_name || "this sensor"}?` : ""}
        detail="The sensor itself stays. Only the 3D marker on the model is removed."
        confirmLabel="Delete tag"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ── History tab ───────────────────────────────────────────────────────

function HistoryTab({ sensor }: { sensor: Sensor }) {
  const points = useMemo(() => {
    const readings = sensor.data_history?.readings ?? [];
    return readings
      .map((r) => {
        const value = (r as { value?: unknown }).value;
        const ts = (r as { ts?: string }).ts;
        return typeof value === "number"
          ? { value, tsLabel: ts ? new Date(ts).toLocaleTimeString() : "", ts: ts || "" }
          : null;
      })
      .filter((p): p is { value: number; tsLabel: string; ts: string } => p !== null);
  }, [sensor.data_history]);

  if (points.length === 0) {
    return (
      <div className="p-4">
        <div className="rounded-lg border border-dashed border-surface-700 px-4 py-6 text-center text-xs text-surface-500">
          No readings recorded yet.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      <div className="rounded-lg border border-surface-700/50 bg-surface-900/40 p-2">
        <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-surface-400">
          Reading history · last {points.length}
        </div>
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 6, left: -22, bottom: 0 }}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis dataKey="tsLabel" tick={{ fontSize: 9, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={20} />
              <YAxis tick={{ fontSize: 9, fill: "#64748b" }} domain={["auto", "auto"]} width={40} />
              <Tooltip
                contentStyle={{ backgroundColor: "rgba(15,23,42,0.95)", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }}
              />
              <Line type="monotone" dataKey="value" stroke="#a5b4fc" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-lg border border-surface-700/50 bg-surface-900/40">
        <div className="border-b border-surface-700/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-surface-400">
          Recent readings
        </div>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-surface-900/95 text-[9px] uppercase tracking-wider text-surface-500">
              <tr>
                <th className="px-3 py-1.5 text-left">Time</th>
                <th className="px-3 py-1.5 text-right">Value{sensor.unit ? ` (${sensor.unit})` : ""}</th>
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p, i) => (
                <tr key={i} className="border-t border-surface-700/40">
                  <td className="px-3 py-1 font-mono text-surface-400">{p.ts}</td>
                  <td className="px-3 py-1 text-right font-mono text-surface-100">{p.value.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
